import { DialogComponent } from '@theme/dialog';

/**
 * Alert me drawer: customer form submits into an in-drawer iframe (same pattern as
 * customize-drawer newsletter). Shopify spam protection may serve an hCaptcha /
 * “verify connection” page — the iframe expands so the shopper can complete it.
 *
 * @extends {DialogComponent}
 */
class WaitlistDrawerComponent extends DialogComponent {
  requiredRefs = ['dialog', 'content'];

  connectedCallback() {
    super.connectedCallback();
    this.#form = this.querySelector('form');
    this.#formWrap = this.querySelector('[data-state="form"]');
    this.#successWrap = this.querySelector('[data-state="success"]');
    this.#thankYouEl = this.querySelector('[ref="thankYouMessage"]');
    this.#validationEl = this.querySelector('[ref="validationSuccess"]');
    this.#iframeHost = this.querySelector('[data-waitlist-iframe-host]');
    this.#challengeNote = this.querySelector('[data-waitlist-challenge-note]');
    if (this.#form) {
      this.#form.addEventListener('submit', this.#handleSubmit);
    }
    this.addEventListener('dialog:close', this.#resetState);
  }

  disconnectedCallback() {
    this.removeEventListener('dialog:close', this.#resetState);
    if (this.#form) {
      this.#form.removeEventListener('submit', this.#handleSubmit);
    }
    this.#detachLoadListener();
    super.disconnectedCallback();
  }

  #resetState = () => {
    this.#awaiting = false;
    this.#detachLoadListener();
    this.#clearTimeout();

    if (this.#formWrap && this.#successWrap) {
      this.#formWrap.hidden = false;
      this.#successWrap.hidden = true;
    }
    if (this.#form) {
      this.#form.removeAttribute('target');
      this.#form.reset();
    }
    const submitBtn = this.#form?.querySelector('button[type="submit"]');
    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = submitBtn.dataset.submitLabel || 'VALIDATE';
    }

    this.#clearChallengeUi();
    if (this.#iframeHost) {
      this.#iframeHost.hidden = true;
    }
  };

  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {HTMLElement | null} */
  #formWrap = null;
  /** @type {HTMLElement | null} */
  #successWrap = null;
  /** @type {HTMLElement | null} */
  #thankYouEl = null;
  /** @type {HTMLElement | null} */
  #validationEl = null;
  /** @type {HTMLElement | null} */
  #iframeHost = null;
  /** @type {HTMLElement | null} */
  #challengeNote = null;
  /** @type {HTMLIFrameElement | null} */
  #iframe = null;
  #awaiting = false;
  /** @type {(() => void) | null} */
  #loadListener = null;
  /** @type {ReturnType<typeof setTimeout> | null} */
  #timeoutId = null;

  #clearTimeout() {
    if (this.#timeoutId != null) {
      clearTimeout(this.#timeoutId);
      this.#timeoutId = null;
    }
  }

  #clearChallengeUi() {
    this.#challengeNote?.setAttribute('hidden', '');
    this.#iframeHost?.classList.remove('is-challenge-active');
  }

  #detachLoadListener() {
    if (this.#loadListener && this.#iframe) {
      this.#iframe.removeEventListener('load', this.#loadListener);
    }
    this.#loadListener = null;
  }

  /**
   * @param {Document | null | undefined} doc
   * @param {string} href
   */
  #isBotChallenge(doc, href) {
    const h = href.toLowerCase();
    if (h.includes('/challenge')) return true;
    const text = doc?.body?.innerText || '';
    return /verified before you continue|verify your connection|needs to be verified|hcaptcha|i.?m not a robot/i.test(
      text
    );
  }

  /**
   * @param {string} href
   * @param {string} token
   */
  #urlIndicatesSuccess(href, token) {
    try {
      const u = new URL(href, window.location.origin);
      if (u.searchParams.get(token) === 'ok') return true;
    } catch {
      /* ignore */
    }
    return href.includes(`${token}=ok`);
  }

  #ensureIframe() {
    if (!this.#iframeHost) return null;
    if (!this.#iframe) {
      const f = document.createElement('iframe');
      f.name = `waitlistTarget-${this.id || 'drawer'}`;
      f.title = 'Alert me signup';
      f.setAttribute('tabindex', '-1');
      this.#iframeHost.appendChild(f);
      this.#iframe = f;
    }
    return this.#iframe;
  }

  #finishSuccess(submitBtn, defaultLabel) {
    if (!this.#awaiting) return;
    this.#awaiting = false;
    this.#clearTimeout();
    this.#detachLoadListener();
    this.#clearChallengeUi();
    this.#form?.removeAttribute('target');
    if (this.#iframeHost) this.#iframeHost.hidden = true;

    const validationLine = this.dataset.validationSuccessText || 'VALIDATION SUCCESS';
    if (this.#validationEl) {
      this.#validationEl.textContent = validationLine;
    }
    const productTitle = this.dataset.productTitle || 'this product';
    const prefix = this.dataset.thankYouPrefix || 'Thank you, you have been added to the alert list for';
    if (this.#thankYouEl) {
      this.#thankYouEl.textContent = `${prefix} ${productTitle}`.trim();
    }
    if (this.#formWrap && this.#successWrap) {
      this.#formWrap.hidden = true;
      this.#successWrap.hidden = false;
    }

    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = defaultLabel;
    }
  }

  /**
   * @param {HTMLButtonElement | null} submitBtn
   * @param {string} defaultLabel
   * @param {string} [message]
   */
  #finishError(submitBtn, defaultLabel, message) {
    if (!this.#awaiting) return;
    this.#awaiting = false;
    this.#clearTimeout();
    this.#detachLoadListener();
    this.#clearChallengeUi();
    this.#form?.removeAttribute('target');
    if (this.#iframeHost) this.#iframeHost.hidden = true;

    if (submitBtn) {
      submitBtn.disabled = false;
      submitBtn.textContent = defaultLabel;
    }
    const msg =
      message && !/translation\s+missing/i.test(message)
        ? message
        : 'Something went wrong. Please try again.';
    alert(msg);
  }

  #handleSubmit = (e) => {
    e.preventDefault();
    if (!this.#form || !this.#formWrap || !this.#successWrap || !this.#thankYouEl) return;
    if (!this.#iframeHost) return;

    if (!this.#form.reportValidity()) return;

    const iframe = this.#ensureIframe();
    if (!iframe) {
      alert('Something went wrong. Please try again.');
      return;
    }

    const submitBtn = /** @type {HTMLButtonElement | null} */ (
      this.#form.querySelector('button[type="submit"]')
    );
    const defaultLabel = submitBtn?.dataset.submitLabel || submitBtn?.textContent?.trim() || 'VALIDATE';
    if (submitBtn) {
      submitBtn.disabled = true;
      submitBtn.textContent = submitBtn.dataset.loadingText || 'VALIDATING...';
    }

    this.#detachLoadListener();
    this.#clearTimeout();
    this.#clearChallengeUi();
    this.#awaiting = true;
    this.#iframeHost.hidden = false;
    this.#iframeHost.classList.add('is-challenge-active');

    this.#loadListener = () => {
      if (!this.#awaiting || !this.#iframe) return;

      let href = '';
      try {
        href = this.#iframe.contentWindow?.location?.href || '';
      } catch {
        return;
      }

      if (!href || href === 'about:blank') return;

      const doc = this.#iframe.contentWindow?.document;

      if (
        this.#urlIndicatesSuccess(href, 'waitlist_signed_up') ||
        /[?&]customer_posted=true/i.test(href) ||
        /[?&]contact_posted=true/i.test(href)
      ) {
        this.#finishSuccess(submitBtn, defaultLabel);
        return;
      }

      if (doc && this.#isBotChallenge(doc, href)) {
        this.#challengeNote?.removeAttribute('hidden');
        this.#iframeHost?.classList.add('is-challenge-active');
        return;
      }

      let errText = '';
      try {
        const errorEl = doc?.querySelector(
          '.form__message--error, .errors, .note--error, .field__input--error'
        );
        errText = errorEl?.textContent?.replace(/\s+/g, ' ').trim() || '';
      } catch {
        /* ignore */
      }

      if (errText) {
        this.#finishError(submitBtn, defaultLabel, errText);
        return;
      }

      try {
        const bodyText = doc?.body?.innerText || '';
        const successNote = doc?.querySelector('.form__message--success, .note--success');
        if (
          successNote ||
          (/thank you|thanks for subscribing|you.?re subscribed|successfully subscribed/i.test(
            bodyText
          ) &&
            !/error|invalid|could not/i.test(bodyText))
        ) {
          this.#finishSuccess(submitBtn, defaultLabel);
        }
      } catch {
        /* ignore */
      }
    };

    this.#iframe.addEventListener('load', this.#loadListener);
    this.#form.target = iframe.name;
    this.#form.submit();
  };
}

if (!customElements.get('waitlist-drawer-component')) {
  customElements.define('waitlist-drawer-component', WaitlistDrawerComponent);
}

export { WaitlistDrawerComponent };
